import { ChangeEvent, FC, useMemo } from 'react';

import { Box, css, styled } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { trackDateInputUsageAnalyticsEvent } from 'analytics/events/input-usage';
import moment, { Moment } from 'moment';

import { useController, useFormContext, useWatch } from 'react-hook-form';

import { useStores } from 'mobx/hooks/useStores';

import { defaultMinDateInput } from 'utils/DateUtils';

import { DrugSpecificProtocol } from 'models/DrugSpecific';
import ScheduledProtocol from 'models/ScheduledProtocol';

import { DrugSpecificProtocolForm } from 'views/Modals/ScheduledProtocolModal';
import { drugSpecificEndOptions } from 'views/Widgets/DrugSpecificProtocolFields.constants';
import EndProtocolOptions, {
  defaultEndDateDurationUnitOptions
} from 'views/Widgets/EndProtocolOptions';
import { RHFStyledInput } from 'views/Widgets/StyledInput';

import Icon from 'components/Icons/Icon';

import { Text } from 'components/UIkit/atoms/Text';

export const DrugSpecificProtocolActivationFields: FC = () => {
  const { control, register, formState } = useFormContext<DrugSpecificProtocolForm>();
  const { constantsStore } = useStores();
  const { field, fieldState } = useController({
    name: 'selectedStartDate',
    control,
    shouldUnregister: false,
    rules: {
      validate: (value) => {
        if (moment(value).isBefore(defaultMinDateInput)) {
          return false;
        }

        return moment(value).isValid();
      }
    }
  });
  const [drug, type, selectedStartDate, selectedEndOption, selectedStartHour, weekdays] = useWatch({
    control,
    name: [
      'drug',
      'type',
      'selectedStartDate',
      'selectedEndOption',
      'selectedStartHour',
      'weekdays'
    ]
  });

  const isDateTimePickerError = Boolean(fieldState.error);

  const currentDrugHourlyTypeId = constantsStore.getSpecificDrugTypeId(
    drug?.value,
    DrugSpecificProtocol.Hourly
  );
  const currentDrugCustomWithoutTypeId = constantsStore.getSpecificDrugTypeId(
    drug?.value,
    DrugSpecificProtocol.SpecificTimesHourlyWithoutHospitalization
  );

  const currentDrugCustomWithTypeId = constantsStore.getSpecificDrugTypeId(
    drug?.value,
    DrugSpecificProtocol.SpecificTimesHourlyWithHospitalization
  );

  const isCustom =
    type?.value === currentDrugCustomWithoutTypeId || type?.value === currentDrugCustomWithTypeId;
  const isHourlyType = type?.value === currentDrugHourlyTypeId;
  const isSelectedEndOptionDisabled =
    selectedEndOption?.value === ScheduledProtocol.END_TYPE_DURATION && isHourlyType;

  const isDurationUnitFieldsDisabled =
    selectedEndOption?.value === ScheduledProtocol.END_TYPE_DURATION && isHourlyType;

  const firstReportMoment: Moment | undefined = useMemo(() => {
    if (!drug?.value || !type?.value || !selectedStartDate) {
      return;
    }
    const activation = moment(selectedStartDate);
    if (!activation.isValid()) {
      return;
    }
    if (isHourlyType && !isCustom) {
      return activation;
    }

    if (isCustom) {
      const reportProtocolTemplate = constantsStore.getSpecificDrugTemplate(drug.value, type.value);
      const reportTimes: string[] = reportProtocolTemplate?.reportTimes || [];
      if (reportTimes.length === 0) {
        return;
      }

      const sorted = reportTimes.slice().sort();
      const normalizedActivation = activation.clone().second(0).millisecond(0);

      for (const time of sorted) {
        const [hh, mm] = time.split(':').map(Number);
        const candidate = normalizedActivation.clone().hour(hh).minute(mm);
        if (!candidate.isBefore(normalizedActivation)) {
          return candidate;
        }
      }

      const [hh, mm] = sorted[0].split(':').map(Number);
      return normalizedActivation.clone().add(1, 'day').hour(hh).minute(mm);
    }
    const base = activation
      .clone()
      .hour(selectedStartHour?.value!)
      .minute(0)
      .second(0)
      .millisecond(0);
    const delta = (weekdays - activation.day() + 7) % 7;
    let weeklyDate = base.clone().add(delta, 'days');

    // Trying to set for today, but the time has already passed
    if (delta === 0 && weeklyDate.isBefore(moment())) {
      weeklyDate = weeklyDate.add(7, 'days');
    }
    return weeklyDate;
  }, [
    drug?.value,
    type?.value,
    selectedStartDate,
    selectedStartHour?.value,
    weekdays,
    isHourlyType,
    isCustom,
    constantsStore
  ]);

  const helperText = useMemo(() => {
    if (!firstReportMoment) {
      return;
    }
    return `Patient’s first report will be ${firstReportMoment.format(
      'dddd'
    )}, ${firstReportMoment.format('L')} at ${firstReportMoment.format('hh:mm A')}.`;
  }, [firstReportMoment]);
  return (
    <>
      <Text variant="body2" color="secondary" display="inline-block" textTransform="uppercase">
        Report Activation
      </Text>

      <StyledSection>
        {isHourlyType || isCustom ? (
          <Box width="100%">
            <Text color={isDateTimePickerError ? 'error' : 'secondary'}>
              Activation of Protocol
            </Text>

            <LocalizationProvider
              dateAdapter={AdapterMoment}
              localeText={{ todayButtonLabel: 'Now' }}
            >
              <StyledDateTimePicker
                value={moment(field.value)}
                onChange={(newValue) => {
                  trackDateInputUsageAnalyticsEvent(
                    newValue.toString(),
                    selectedStartDate?.toString(),
                    'Activation of Protocol'
                  );

                  field.onChange(moment(newValue.toDate()));
                }}
                slotProps={{
                  actionBar: {
                    actions: ['today']
                  }
                }}
                slots={{ openPickerIcon: Icon.Calendar }}
                timeSteps={{ minutes: 15 }}
                disablePast
                closeOnSelect={false}
                isError={isDateTimePickerError}
                inputRef={field.ref}
              />
            </LocalizationProvider>
          </Box>
        ) : (
          <RHFStyledInput
            key="date"
            type="date"
            label="Activation of Protocol"
            isRequired
            register={register}
            error={Boolean(formState.errors.selectedStartDate)}
            name="selectedStartDate"
            onChange={(event) => {
              trackDateInputUsageAnalyticsEvent(
                (event as ChangeEvent<HTMLInputElement>).target.value,
                selectedStartDate as string,
                'Activation of Protocol'
              );
            }}
            min={defaultMinDateInput}
          />
        )}
        <Text variant="helper-text" color="secondary" mt={2}>
          {helperText}
        </Text>
      </StyledSection>
      <EndProtocolOptions
        minEndDate={selectedStartDate as string}
        endOptions={drugSpecificEndOptions}
        endDateDurationUnitOptions={defaultEndDateDurationUnitOptions}
        isSelectedEndOption={isSelectedEndOptionDisabled}
        isDurationUnitFieldsDisabled={isDurationUnitFieldsDisabled}
      />
    </>
  );
};

const StyledSection = styled(Box)(
  ({ theme }) => css`
        margin: ${theme.spacing(20, 0)};
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 100%;

        .styled-select {
            width: 100%;
        }

        .styled-input {
            width: 100%;
        }
    }
    `
);

const StyledDateTimePicker = styled(DateTimePicker, {
  shouldForwardProp: (prop) => prop !== 'isError'
})<{ isError: boolean }>(
  ({ theme, isError }) => css`
    &.MuiFormControl-root.MuiTextField-root {
      width: 100%;

      .MuiInputBase-root.MuiOutlinedInput-root {
        border-radius: 0 16px 16px 16px;

        &.Mui-focused {
          fieldset.MuiOutlinedInput-notchedOutline {
            border-color: ${isError ? theme.palette.error.main : theme.palette.primary.main};
          }
        }

        input {
          font-size: 16px;
          font-weight: 400;
          box-sizing: border-box;
          height: 45px;
          padding: ${theme.spacing(4, 12)};
        }
      }

      svg {
        color: ${theme.palette.text.primary};
      }

      fieldset.MuiOutlinedInput-notchedOutline {
        border-color: ${isError ? theme.palette.error.main : theme.palette.natural.border};
        border-width: 1px;
      }

      legend {
        width: auto;
      }

      .MuiInputAdornment-root .MuiButtonBase-root.MuiIconButton-root:hover {
        background-color: initial;
      }
    }
  `
);
